import { Box, Flex, Input, Text } from '@lego/klik-ui';
import { useMemo, useState } from 'react';

type Option = {
  label: string;
  value: string;
};

type SearchableSelectProps = {
  options: Option[];
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
};

export const SearchableSelect = ({
  options,
  placeholder = 'Search...',
  value,
  onChange,
}: SearchableSelectProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  const selectedLabel = options.find((o) => o.value === value)?.label;

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md">
      <Input
        border="none"
        borderBottom="1px solid"
        borderBottomColor="gray.200"
        borderBottomRadius="none"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        placeholder={placeholder}
        size="sm"
        value={searchQuery}
      />
      <Box maxH="200px" overflowY="auto">
        {filteredOptions.length === 0 ? (
          <Text color="gray.500" fontSize="sm" p={2}>
            No results found.
          </Text>
        ) : (
          filteredOptions.map((option) => (
            <Flex
              _hover={{ bg: 'gray.100' }}
              bg={option.value === value ? 'blue.50' : undefined}
              cursor="pointer"
              fontSize="sm"
              key={option.value}
              onClick={() => onChange(option.value)}
              px={3}
              py={2}
            >
              <Text fontWeight={option.value === value ? 'bold' : 'normal'}>{option.label}</Text>
            </Flex>
          ))
        )}
      </Box>
      {selectedLabel && (
        <Box bg="gray.50" borderTop="1px solid" borderTopColor="gray.200" px={3} py={1}>
          <Text color="gray.600" fontSize="xs">
            Selected: {selectedLabel}
          </Text>
        </Box>
      )}
    </Box>
  );
};
